import _toConsumableArray from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/toConsumableArray";

/**
 * @license
 * Copyright 2017 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as util from '../util';
export function assertParamsValid(input, begin, size) {
  var inputRank = input.shape.length;
  util.assert(inputRank === begin.length, function () {
    return "Error in slice".concat(inputRank, "D: Length of begin ").concat(begin, " must ") + "match the rank of the array (".concat(inputRank, ").");
  });
  util.assert(inputRank === size.length, function () {
    return "Error in slice".concat(inputRank, "D: Length of size ").concat(size, " must ") + "match the rank of the array (".concat(inputRank, ").");
  });

  var _loop = function _loop(i) {
    util.assert(begin[i] + size[i] <= input.shape[i], function () {
      return "Error in slice".concat(inputRank, "D: begin[").concat(i, "] + size[").concat(i, "] ") + "(".concat(begin[i] + size[i], ") would overflow input.shape[").concat(i, "] (").concat(input.shape[i], ")");
    });
  };

  for (var i = 0; i < inputRank; ++i) {
    _loop(i);
  }
}
/** Converts a binary mask to an array of axes. Used in stridedSlice(). */

export function maskToAxes(mask) {
  var axes = [];
  var axis = 0;

  while (mask > 0) {
    if (mask & 1) {
      axes.push(axis);
    }

    mask /= 2;
    axis++;
  }

  return axes;
}
/** Computes the output shape given the strided slice params. */

export function computeOutShape(begin, end, strides) {
  var size = [];

  for (var axis = 0; axis < begin.length; axis++) {
    size[axis] = Math.ceil((end[axis] - begin[axis]) / strides[axis]);
  }

  return size;
} // Creates full selection at the elided dimensions. If the dimension matches
// the ellipsis mask, override the current stride value. Otherwise, insert.

export function stridesWithElidedDims(strides, ellipsisInsertionIndex, numElidedAxes, inputShape) {
  var newStrides = _toConsumableArray(strides);

  for (var i = newStrides.length; i < inputShape.length; i++) {
    newStrides.push(1);
  }

  for (var _i = 0; _i < numElidedAxes; _i++) {
    if (_i === 0) {
      newStrides[ellipsisInsertionIndex] = 1;
    } else {
      newStrides.splice(ellipsisInsertionIndex, 0
      /* num elements to delete */
      , 1
      /* element to add */
      );
      newStrides.pop();
    }
  }

  return newStrides;
}

function unnormalizeAxis(ellipsisInsertionIndex, numElidedAxes, normalizedAxis) {
  if (normalizedAxis <= ellipsisInsertionIndex) {
    return normalizedAxis;
  }

  return normalizedAxis - (numElidedAxes - 1);
}

function getElidedAxes(numElidedAxes, ellipsisInsertionIndex) {
  var elidedAxes = [];

  for (var i = 0; i < numElidedAxes; i++) {
    elidedAxes.push(ellipsisInsertionIndex + i);
  }

  return elidedAxes;
} // Normalize the start, end and strides.


export function getNormalizedAxes(inputShape, ellipsisAxes, numInterpolatedAxes, begin, end, strides, beginMask, endMask, ellipsisMask) {
  var inputRank = inputShape.length;
  var normalizedBegin = new Array(inputRank),
      normalizedEnd = new Array(inputRank),
      normalizedStrides = new Array(inputRank);

  if (ellipsisAxes.length && numInterpolatedAxes > 0) {
    var fullIndex = ellipsisAxes[0]; // The ellipsis applies to the masked index as well as any dimensions
    // that are interpolated.

    var numElidedAxes = numInterpolatedAxes + 1;
    normalizedBegin = startIndicesWithElidedDims(beginMask, fullIndex, numElidedAxes, begin, inputShape);
    normalizedEnd = stopIndicesWithElidedDims(endMask, fullIndex, numElidedAxes, end, inputShape);
    normalizedStrides = stridesWithElidedDims(strides, fullIndex, numElidedAxes, inputShape);
  } else {
    for (var axis = 0; axis < inputRank; axis++) {
      normalizedBegin[axis] = startForAxis(beginMask, begin, strides, inputShape, axis, ellipsisMask);
      normalizedEnd[axis] = stopForAxis(endMask, end, strides, inputShape, axis, ellipsisMask);
      normalizedStrides[axis] = stridesForAxis(strides, axis, ellipsisMask);
    }
  }

  return {
    begin: normalizedBegin,
    end: normalizedEnd,
    strides: normalizedStrides
  };
} // Creates full selection at the elided dimensions. If the dimension matches
// the ellipsis mask, override the current start value. Otherwise, insert.

export function startIndicesWithElidedDims(beginMask, ellipsisInsertionIndex, numElidedAxes, originalBegin, inputShape) {
  var newIndices = _toConsumableArray(inputShape);

  var elidedAxes = getElidedAxes(numElidedAxes, ellipsisInsertionIndex);

  for (var axis = 0; axis < newIndices.length; axis++) {
    if (elidedAxes.indexOf(axis) > -1) {
      newIndices[axis] = 0;
    } else {
      var originalAxis = unnormalizeAxis(ellipsisInsertionIndex, numElidedAxes, axis);
      var originalValue = originalBegin[originalAxis];

      if (beginMask & 1 << originalAxis) {
        originalValue = 0;
      }

      newIndices[axis] = originalValue;
    }
  }

  return newIndices;
} // Creates full selection at the elided dimensions. If the dimension matches
// the ellipsis mask, override the current stop value. Otherwise, insert.

export function stopIndicesWithElidedDims(endMask, ellipsisInsertionIndex, numElidedAxes, originalEnd, inputShape) {
  var newIndices = _toConsumableArray(inputShape);

  var elidedAxes = getElidedAxes(numElidedAxes, ellipsisInsertionIndex);

  for (var axis = 0; axis < newIndices.length; axis++) {
    if (elidedAxes.indexOf(axis) > -1) {
      newIndices[axis] = Number.MAX_SAFE_INTEGER;
    } else {
      var originalAxis = unnormalizeAxis(ellipsisInsertionIndex, numElidedAxes, axis);
      var originalValue = originalEnd[originalAxis];

      if (endMask & 1 << originalAxis) {
        originalValue = Number.MAX_SAFE_INTEGER;
      }

      newIndices[axis] = originalValue;
    }
  }

  for (var i = 0; i < newIndices.length; i++) {
    // Handle negative indices
    var axisSize = inputShape[i];

    if (newIndices[i] < 0) {
      newIndices[i] += axisSize;
    }

    newIndices[i] = util.clamp(0, newIndices[i], inputShape[i]);
  }

  return newIndices;
}
export function stridesForAxis(strides, axis, ellipsisMask) {
  var stride = strides[axis];

  if (ellipsisMask & 1 << axis || stride == null) {
    stride = 1;
  }

  return stride;
}
export function startForAxis(beginMask, startIndices, strides, inputShape, axis, ellipsisMask) {
  // Begin with the specified index
  var start = startIndices[axis];
  var stride = strides[axis] || 1; // Check the axis bit from right of masked axes, or the begin index is not set
  // for the axis.

  if (beginMask & 1 << axis || ellipsisMask & 1 << axis || start == null) {
    if (stride > 0) {
      // Forward iteration - use the first element. These values will get
      // clamped below (Note: We could have set them to 0 and axis_size-1, but
      // use lowest() and max() to maintain symmetry with StopForAxis())
      start = Number.MIN_SAFE_INTEGER;
    } else {
      // Backward iteration - use the last element.
      start = Number.MAX_SAFE_INTEGER;
    }
  } // Handle negative indices


  var axisSize = inputShape[axis];

  if (start < 0) {
    start += axisSize;
  } // Clamping


  start = util.clamp(0, start, axisSize - 1);
  return start;
}
export function stopForAxis(endMask, stopIndices, strides, inputShape, axis, ellipsisMask) {
  // Begin with the specified index
  var stop = stopIndices[axis];
  var stride = strides[axis] || 1; // Check the axis bit from right of masked axes, or if the stop index is not
  // set for this axis.

  if (endMask & 1 << axis || ellipsisMask & 1 << axis || stop == null) {
    if (stride > 0) {
      // Forward iteration - use the last element. These values will get
      // clamped below
      stop = Number.MAX_SAFE_INTEGER;
    } else {
      // Backward iteration - use the first element.
      stop = Number.MIN_SAFE_INTEGER;
    }
  } // Handle negative indices


  var axisSize = inputShape[axis];

  if (stop < 0) {
    stop += axisSize;
  } // Clamping
  // Because the end index points one past the last element, we need slightly
  // different clamping ranges depending on the direction.


  if (stride > 0) {
    // Forward iteration
    stop = util.clamp(0, stop, axisSize);
  } else {
    // Backward iteration
    stop = util.clamp(-1, stop, axisSize - 1);
  }

  return stop;
}
/**
 * Returns true if the slice occupies a continous set of elements in the
 * 'flat' space.
 */

export function isSliceContinous(shape, begin, size) {
  // Index of the first axis that has size > 1.
  var firstNonOneAxis = size.length;

  for (var i = 0; i < size.length; i++) {
    if (size[i] > 1) {
      firstNonOneAxis = i;
      break;
    }
  }

  for (var _i2 = firstNonOneAxis + 1; _i2 < size.length; _i2++) {
    if (begin[_i2] > 0 || size[_i2] !== shape[_i2]) {
      return false;
    }
  }

  return true;
}
export function computeFlatOffset(begin, strides) {
  var flatOffset = begin.length > 0 ? begin[begin.length - 1] : 1;

  for (var i = 0; i < begin.length - 1; i++) {
    flatOffset += begin[i] * strides[i];
  }

  return flatOffset;
}
export function parseSliceParams(x, begin, size) {
  // The following logic allows for more ergonomic calls.
  var begin_;
  var xRank = x.shape.length;

  if (typeof begin === 'number') {
    begin_ = [begin].concat(_toConsumableArray(new Array(xRank - 1).fill(0)));
  } else if (begin.length < xRank) {
    begin_ = begin.concat(new Array(xRank - begin.length).fill(0));
  } else {
    begin_ = begin.slice();
  }

  begin_.forEach(function (d) {
    util.assert(d !== -1, function () {
      return 'slice() does not support negative begin indexing.';
    });
  });
  var size_;

  if (size == null) {
    size_ = new Array(xRank).fill(-1);
  } else if (typeof size === 'number') {
    size_ = [size].concat(_toConsumableArray(new Array(xRank - 1).fill(-1)));
  } else if (size.length < xRank) {
    size_ = size.concat(new Array(xRank - size.length).fill(-1));
  } else {
    size_ = size;
  }

  size_ = size_.map(function (d, i) {
    if (d >= 0) {
      return d;
    } else {
      util.assert(d === -1, function () {
        return "Negative size values should be exactly -1 but got " + "".concat(d, " for the slice() size at index ").concat(i, ".");
      });
      return x.shape[i] - begin_[i];
    }
  });
  return [begin_, size_];
}
export function sliceInfo(xShape, begin, end, strides, beginMask, endMask, ellipsisMask, newAxisMask, shrinkAxisMask) {
  // make a copy because it may be modified further down.
  var $begin = begin.slice();
  var $end = end.slice();
  var $strides = strides;

  if (strides == null) {
    $strides = new Array($begin.length);
  }

  var ellipsisAxes = maskToAxes(ellipsisMask);

  if (ellipsisAxes.length > 1) {
    throw new Error('Multiple ellipses in slice is not allowed.');
  }

  if (ellipsisMask !== 0 && newAxisMask !== 0) {
    throw new Error('Using both ellipsisMask and newAxisMask is not yet supported.');
  }

  if (ellipsisMask !== 0 && shrinkAxisMask !== 0) {
    throw new Error('Using both ellipsisMask and shrinkAxisMask is not yet supported.');
  }

  var numInterpolatedAxes = xShape.length - $begin.length; // Expand the dims of x based on the newAxisMask.

  var expandAxes = maskToAxes(newAxisMask);
  var newShape = xShape.slice();
  expandAxes.forEach(function (axis) {
    $begin[axis] = 0;
    $end[axis] = 1;
    newShape.splice(axis, 0, 1);
  });

  var _getNormalizedAxes = getNormalizedAxes(newShape, ellipsisAxes, numInterpolatedAxes, $begin, $end, $strides, beginMask, endMask, ellipsisMask),
      normalizedBegin = _getNormalizedAxes.begin,
      normalizedEnd = _getNormalizedAxes.end,
      normalizedStrides = _getNormalizedAxes.strides;

  $begin = normalizedBegin;
  $end = normalizedEnd;
  $strides = normalizedStrides;
  var shrinkAxes = maskToAxes(shrinkAxisMask); // Adjust the ends based on the shrink mask.

  shrinkAxes.forEach(function (axis) {
    $end[axis] = $begin[axis] + 1;
    $strides[axis] = 1;
  }); // Figure out the output shape.

  var size = computeOutShape($begin, $end, $strides); // Remove the axes based on shrinkMask.

  var outShape = size.filter(function (_, axis) {
    return shrinkAxes.indexOf(axis) === -1;
  });
  var nonStrided = $strides.every(function (v) {
    return v === 1;
  });
  return {
    nonStrided: nonStrided,
    $begin: $begin,
    $end: $end,
    $strides: $strides,
    size: size,
    newShape: newShape,
    outShape: outShape
  };
}